import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import {
    deleteBulkNote,
    deleteNote,
    deleteNoteAsset,
    getNoteReviews,
    getNoteHistory,
    getNote,
    getNoteList,
    patchNote,
    postNote,
    postNoteAsset,
    postNoteHistoryRestore,
    postNoteReview,
    postNoteReviewMessage,
    postNoteReviewReopen,
    postNoteReviewResolve,
    postNoteRevision,
    postNoteWithMarkdown,
} from "utils/request/note";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

export const useNotes = (params) => {
    const spaceUuid = useCurrentSpaceUuid();
    return useQuery(["notes", spaceUuid, params], () => getNoteList(spaceUuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
    });
};

export const useNote = (params) => {
    const spaceUuid = useCurrentSpaceUuid();
    return useQuery(["note", spaceUuid, params?.note_no], () => getNote(spaceUuid, params), {
        enabled: !!params?.note_no,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useNoteCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => postNote(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "notes" });
        },
    });
};

export const useNoteWithMarkdownCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => postNoteWithMarkdown(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "notes" });
        },
    });
};

export const useNoteUpdate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => patchNote(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "note" });
            queryClient.invalidateQueries({ queryKey: "notes" });
        },
    });
};

export const useNoteDelete = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteNote(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "notes" });
        },
    });
};

export const useNoteBulkDelete = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteBulkNote(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "notes" });
        },
    });
};

export const useNoteAssetCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => postNoteAsset(spaceUuid, variables), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "note" });
        },
    });
};

export const useNoteAssetDelete = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteNoteAsset(spaceUuid, variables.assetId), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "note" });
        },
    });
};

export const useNoteHistory = (noteId, options = {}) => {
    const spaceUuid = useCurrentSpaceUuid();
    return useQuery(["noteHistory", spaceUuid, noteId], () => getNoteHistory(spaceUuid, noteId), {
        enabled: !!noteId && options.enabled !== false,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        retry: false,
    });
};

export const useNoteRevisionCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, body }) => postNoteRevision(spaceUuid, noteId, body), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "note" });
            queryClient.invalidateQueries({ queryKey: "noteHistory" });
        },
    });
};

export const useNoteHistoryRestore = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, revisionId, body }) => postNoteHistoryRestore(spaceUuid, noteId, revisionId, body), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "note" });
            queryClient.invalidateQueries({ queryKey: "notes" });
            queryClient.invalidateQueries({ queryKey: "noteHistory" });
        },
    });
};

export const useNoteReviews = (noteId, options = {}) => {
    const spaceUuid = useCurrentSpaceUuid();
    return useQuery(["noteReviews", spaceUuid, noteId], () => getNoteReviews(spaceUuid, noteId), {
        enabled: !!noteId && options.enabled !== false,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        retry: false,
    });
};

export const useNoteReviewCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, body }) => postNoteReview(spaceUuid, noteId, body), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "noteReviews" });
        },
    });
};

export const useNoteReviewMessageCreate = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, threadId, body }) => postNoteReviewMessage(spaceUuid, noteId, threadId, body), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "noteReviews" });
        },
    });
};

export const useNoteReviewResolve = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, threadId }) => postNoteReviewResolve(spaceUuid, noteId, threadId), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "noteReviews" });
        },
    });
};

export const useNoteReviewReopen = () => {
    const queryClient = useQueryClient();
    const spaceUuid = useCurrentSpaceUuid();
    return useMutation(({ noteId, threadId }) => postNoteReviewReopen(spaceUuid, noteId, threadId), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: "noteReviews" });
        },
    });
};
